// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
/**
 * Published sources behind every number the app states about energy and
 * macronutrients (App Review, Guideline 1.4.1, 2026-10-24: "the app provides
 * health or medical recommendations as calorie intake without citations").
 *
 * The ids key the localized title/detail in `moreProfile.goals.sources.items`
 * (onboarding.en.ts / onboarding.de.ts); the URLs are not translated. Every
 * URL was fetched on 2026-10-24. Keep this list in step with the rules in
 * infra-cdk/lambdas/bitebalance-api/shared/targets.ts (the owner of the
 * calculation) — a new rule needs its source here, not a bare number in the UI.
 */
export const NUTRITION_SOURCE_IDS = ["mifflin", "compendium", "iom", "dge", "nhlbi", "cdc"];
export const NUTRITION_SOURCE_URLS = {
    /** Mifflin MD, St Jeor ST et al. A new predictive equation for resting energy expenditure in healthy individuals. Am J Clin Nutr 1990;51(2):241-247. */
    mifflin: "https://pubmed.ncbi.nlm.nih.gov/2305711/",
    /** Ainsworth BE et al. 2011 Compendium of Physical Activities: a second update of codes and MET values. Med Sci Sports Exerc 2011;43(8):1575-1581. */
    compendium: "https://pubmed.ncbi.nlm.nih.gov/21681120/",
    /** Institute of Medicine (2005). Dietary Reference Intakes for Energy, Carbohydrate, Fiber, Fat, Fatty Acids, Cholesterol, Protein, and Amino Acids (AMDR; lactation energy). */
    iom: "https://nap.nationalacademies.org/catalog/10490/dietary-reference-intakes-for-energy-carbohydrate-fiber-fat-fatty-acids-cholesterol-protein-and-amino-acids",
    /** Deutsche Gesellschaft für Ernährung: Referenzwerte für die Nährstoffzufuhr — Energie (incl. Stillende +500 kcal/Tag). */
    dge: "https://www.dge.de/wissenschaft/referenzwerte/energie/",
    /** NHLBI / NAASO (2000). The Practical Guide: Identification, Evaluation, and Treatment of Overweight and Obesity in Adults (low-calorie diets 1,000-1,200 kcal/day women, 1,200-1,600 men). */
    nhlbi: "https://www.nhlbi.nih.gov/files/docs/guidelines/prctgd_c.pdf",
    /** CDC: Maternal Diet and Breastfeeding (additional 330-400 kcal/day for well-nourished breastfeeding mothers). */
    cdc: "https://www.cdc.gov/breastfeeding-special-circumstances/hcp/diet-micronutrients/maternal-diet.html",
};
/** Groups shown on the Sources & method screen, in order, with the sources each group cites. */
export const NUTRITION_SOURCE_GROUPS = [
    { id: "target", sources: ["mifflin", "compendium", "dge"] },
    { id: "macros", sources: ["iom", "dge"] },
    { id: "floor", sources: ["nhlbi"] },
    { id: "breastfeeding", sources: ["cdc", "iom", "dge"] },
];
